import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { InsightSectionRenderer } from "@/components/portal/insight-reports/InsightSectionRenderer";
import { ChartSettingsProvider } from "@/components/portal/charts/settings/ChartSettingsProvider";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  canAuthorInsightReports,
  canViewInsightReports,
} from "@/lib/insightReportsAccess";
import {
  describeRefreshShortfall,
  insightReportsApi,
  summariseRefreshFanout,
  type InsightReport,
  type InsightSection,
} from "@/services/api/insightReports";

import { InsightReportBuilder } from "./InsightReportBuilder";

export const Route = createFileRoute("/portal/knowledgebase-sql/insight-reports/$slug")({
  component: InsightReportPage,
});

interface SectionViewProps {
  section: InsightSection;
  onRefresh: (sectionId: string) => void;
  refreshing: boolean;
  refreshDisabled: boolean;
}

/** Thin per-page wrapper around the shared InsightSectionRenderer — this
 * page's own chrome (the per-section refresh button + "data as at" stamp) is
 * a headerExtra slot rendered by every role's template (hero/stat_row/
 * narrative/standard alike — gate-review H2 fix: this chrome varies by
 * SURFACE, not by role, so every section on the full page must be
 * refreshable regardless of its layout role). */
function SectionView({ section, onRefresh, refreshing, refreshDisabled }: SectionViewProps) {
  return (
    <section className="border-b pb-6 last:border-b-0">
      {/* #1024 item 2: `InsightReportPage` now mounts `ChartSettingsProvider`
          (below), so `useResolvedLevels()` resolves the organisation's
          palette here too, matching the same report embedded in a Space
          widget. */}
      <InsightSectionRenderer
        section={section}
        chartHeightClassName="h-80"
        tableMaxHeightClassName="max-h-96"
        maxTableRows={100}
        headingClassName="text-base font-semibold"
        headingLevel="h2"
        bannerVariant="page"
        headerExtra={
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            disabled={refreshDisabled}
            onClick={() => onRefresh(section.id)}
            aria-label="Refresh this section"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        }
      />
    </section>
  );
}

function InsightReportPage() {
  const { slug } = Route.useParams();
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { isOwner, isAdmin, isPlatformAdmin } = usePermissions();
  const canView = canViewInsightReports({ isOwner, isAdmin, isPlatformAdmin });
  const canAuthor = canAuthorInsightReports({ isOwner, isAdmin, isPlatformAdmin });
  const [showBuilder, setShowBuilder] = useState(false);
  const [refreshingSectionId, setRefreshingSectionId] = useState<string | null>(null);
  const [refreshingAll, setRefreshingAll] = useState(false);

  const reportQuery = useQuery<InsightReport>({
    queryKey: ["insight-report-page", slug],
    queryFn: () => insightReportsApi.get(token ?? "", slug),
    enabled: !!token && canView,
  });

  const handleRefresh = async (sectionId: string) => {
    if (!token || refreshingSectionId || refreshingAll) return;
    setRefreshingSectionId(sectionId);
    try {
      await insightReportsApi.refreshSectionAndWait(token, slug, sectionId);
    } catch (err) {
      // A job that RAN and failed RESOLVES and surfaces on the section itself
      // (snapshot.status) once the report refetches below. A throw here is a
      // refresh that was never queued (429 on the enqueue) or a poll that
      // never reached a terminal state — both leave the snapshot untouched,
      // so they must be said out loud or the user sees a stale section and
      // nothing else.
      // Both shapes go through the shared wording so the page and the Space
      // widget say the same thing.
      const outcome = summariseRefreshFanout([sectionId], [{ status: "rejected", reason: err }]);
      const shortfall = describeRefreshShortfall(outcome, 1);
      if (shortfall) toast.warning(shortfall);
    } finally {
      setRefreshingSectionId(null);
      void reportQuery.refetch();
    }
  };

  const handleRefreshAll = async () => {
    if (!token || refreshingSectionId || refreshingAll || !reportQuery.data) return;
    const sectionIds = reportQuery.data.sections.map((s) => s.id);
    if (sectionIds.length === 0) return;
    setRefreshingAll(true);
    try {
      // Bounded fan-out (MAX_CONCURRENT_SECTION_REFRESHES) — the enqueue is
      // per-IP rate limited at 20/min on the data plane, and one Space page
      // may hold several of these reports. A single failed section never
      // blocks the others; a section whose enqueue was refused is reported,
      // because its snapshot stays exactly as it was and would otherwise
      // read as "refreshed fine".
      const outcome = await insightReportsApi.refreshSectionsBounded(token, slug, sectionIds);
      const shortfall = describeRefreshShortfall(outcome, sectionIds.length);
      if (shortfall) toast.warning(shortfall);
    } finally {
      setRefreshingAll(false);
      void reportQuery.refetch();
    }
  };

  if (!canView) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">
          Insight Reports isn't available for your account yet.
        </p>
      </div>
    );
  }

  if (reportQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (!reportQuery.data) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Report not found.</p>
      </div>
    );
  }

  const report = reportQuery.data;
  const sortedSections = [...report.sections].sort((a, b) => a.ordinal - b.ordinal);

  return (
    // #1024 item 2: mounted here (not per-chart) so `useResolvedLevels()`
    // resolves the organisation's palette for both the read-only sections
    // below and the inline builder's live preview — the same provider the
    // Space route (`$spaceId.tsx`) mounts for the embedded widget. No
    // `spaceId`: a standalone Insight Report isn't scoped to one, so this
    // resolves org-level (L1/L2) settings only, same as the builder below.
    <ChartSettingsProvider organisationId={selectedOrganisation?.organisationId}>
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        <Helmet>
          <title>{report.title}</title>
        </Helmet>
        <PageHeader title={report.title} description={report.description ?? undefined}>
          <div className="flex items-center gap-4">
            {sortedSections.length > 0 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={refreshingAll || !!refreshingSectionId}
                onClick={() => void handleRefreshAll()}
              >
                <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshingAll ? "animate-spin" : ""}`} />
                Refresh all
              </Button>
            )}
            {canAuthor && (
              <button
                type="button"
                className="text-sm font-medium text-primary underline-offset-2 hover:underline"
                onClick={() => setShowBuilder((v) => !v)}
              >
                {showBuilder ? "Hide builder" : "Add a section"}
              </button>
            )}
          </div>
        </PageHeader>

        {showBuilder && canAuthor && (
          <InsightReportBuilder
            report={report}
            onReportChanged={() => {
              void reportQuery.refetch();
            }}
          />
        )}

        <div className="space-y-6">
          {sortedSections.length === 0 ? (
            <p className="text-sm text-muted-foreground">This report has no sections yet.</p>
          ) : (
            sortedSections.map((section) => (
              <SectionView
                key={section.id}
                section={section}
                onRefresh={(id) => void handleRefresh(id)}
                refreshing={refreshingSectionId === section.id || refreshingAll}
                refreshDisabled={refreshingAll || !!refreshingSectionId}
              />
            ))
          )}
        </div>
      </div>
    </ChartSettingsProvider>
  );
}
